"use client";

import { useCallback, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import type { Locale } from "@/lib/i18n/config";

const Hero3DScene = dynamic(
  () =>
    import("@/components/home/Hero3DScene").then((mod) => ({
      default: mod.Hero3DScene,
    })),
  { ssr: false },
);

const heroSymbolSrc = "/brand/logo-symbol-256.webp";

type Props = {
  lang: Locale;
};

/**
 * Hero visual lato client.
 *
 * Branch viewport gestiti tramite classi Tailwind responsive
 * (block md:hidden / hidden md:block xl:hidden / hidden xl:block)
 * invece di state JS. Questo elimina hydration mismatch #418:
 * il markup SSR == primo render client su ogni viewport.
 *
 * - Mobile (<768): orb poster statico
 * - Tablet (768-1280): Canvas 3D compatto
 * - Desktop (>=1280): Canvas 3D grande absolute positioned
 *
 * Hero3DScene resta dynamic({ssr:false}) - R3F non e' SSR-safe.
 */
export function HomeHeroVisual({ lang }: Props) {
  const isItalian = lang === "it";
  const [mounted, setMounted] = useState(false);
  const [heroTier, setHeroTier] = useState<
    "mobile" | "tablet" | "desktop" | null
  >(null);
  const [shouldRenderDesktopScene, setShouldRenderDesktopScene] =
    useState(false);
  const [heroSceneProgress, setHeroSceneProgress] = useState(0);
  const [heroSceneReady, setHeroSceneReady] = useState(false);

  // Mount gate: server e client first render mostrano placeholder statico
  // identico → no hydration #418. Dopo mount, state change abilita Canvas R3F.
  useEffect(() => {
    setMounted(true);
  }, []);

  // Viewport tier gate: monta il Canvas R3F SOLO sul branch effettivamente
  // visibile. Mobile (<768) resta poster statico → niente download di
  // three.js/R3F/drei + GLB. Evita anche i Canvas doppi nascosti
  // (tablet branch su desktop e viceversa). Misurato post-mount, quindi
  // SSR e primo render client = heroTier null = nessun Canvas = no #418.
  useEffect(() => {
    const tabletMq = window.matchMedia(
      "(min-width: 768px) and (max-width: 1279px)",
    );
    const desktopMq = window.matchMedia("(min-width: 1280px)");

    const update = () => {
      setHeroTier(
        desktopMq.matches ? "desktop" : tabletMq.matches ? "tablet" : "mobile",
      );
    };

    update();
    tabletMq.addEventListener("change", update);
    desktopMq.addEventListener("change", update);

    return () => {
      tabletMq.removeEventListener("change", update);
      desktopMq.removeEventListener("change", update);
    };
  }, []);

  // Defer desktop Canvas mount fino a prima interazione o timeout -
  // tiene Three.js fuori dal critical path.
  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;

    const revealScene = () => {
      if (cancelled) return;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      setShouldRenderDesktopScene((previous) => {
        if (previous) return previous;
        return true;
      });
    };

    const interactionEvents: Array<keyof WindowEventMap> = [
      "scroll",
      "pointerdown",
      "keydown",
      "mousemove",
    ];

    const handleInteraction = () => {
      revealScene();
      interactionEvents.forEach((eventName) =>
        window.removeEventListener(eventName, handleInteraction),
      );
    };

    interactionEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleInteraction, {
        passive: true,
        once: true,
      });
    });

    timeoutId = window.setTimeout(revealScene, 4800);

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      interactionEvents.forEach((eventName) =>
        window.removeEventListener(eventName, handleInteraction),
      );
    };
  }, []);

  const handleHeroSceneProgress = useCallback((value: number) => {
    setHeroSceneProgress(value);
  }, []);

  const handleHeroSceneReady = useCallback(() => {
    setHeroSceneProgress(100);
    setHeroSceneReady(true);
  }, []);

  return (
    <div className="mt-4 md:mt-5 xl:mt-0">
      {/* Mobile (<768px): static orb poster */}
      <div className="block md:hidden relative z-10 pointer-events-none mt-10 mb-0 h-40 flex items-center justify-center">
        <div className="relative">
          <div className="absolute inset-[-12px] rounded-full bg-primary/52 blur-3xl" />
          <div className="absolute inset-2 rounded-full bg-primary/30 blur-[36px]" />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={heroSymbolSrc}
            srcSet="/brand/logo-symbol-96.webp 96w, /brand/logo-symbol-256.webp 256w"
            sizes="96px"
            alt={isItalian ? "Dimensione 4 symbol" : "Dimensione 4 symbol"}
            width={128}
            height={128}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="relative w-[96px] h-[96px] object-contain opacity-100 drop-shadow-[0_0_32px_hsl(var(--primary)/0.78)] animate-hero-orb"
          />
        </div>
      </div>

      {/* Tablet (768-1280px): Canvas 3D compatto */}
      <div className="hidden md:flex xl:hidden relative z-10 pointer-events-none mt-6 justify-center">
        <div className="relative h-[280px] w-full max-w-[500px] overflow-hidden rounded-[2rem] border border-primary/10 bg-[radial-gradient(circle_at_center,hsl(var(--primary)/0.08),transparent_68%)]">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/25" />
          <div className="flex h-full w-full items-center justify-center">
            <div className="relative flex h-[220px] w-[220px] items-center justify-center">
              {/* Placeholder rings: fade-out quando il 3D è pronto */}
              <div
                className={`pointer-events-none absolute inset-0 flex items-center justify-center rounded-full border border-primary/15 bg-[radial-gradient(circle_at_center,hsl(var(--primary)/0.18),transparent_65%)] transition-opacity duration-300 ${
                  heroSceneReady ? "opacity-0" : "opacity-100"
                }`}
              >
                <div className="h-[168px] w-[168px] rounded-full border border-primary/18 bg-[radial-gradient(circle_at_center,hsl(var(--primary)/0.24),transparent_70%)]" />
              </div>
              {heroTier === "tablet" ? (
                <div
                  className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                    heroSceneReady ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <Hero3DScene
                    className="flex h-full w-full items-center justify-center pointer-events-none"
                    canvasClassName="mx-auto h-[280px] w-[280px] md:h-[320px] md:w-[320px] pointer-events-none"
                    onLoadingProgress={handleHeroSceneProgress}
                    onSceneReady={handleHeroSceneReady}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop (>=1280px): Canvas 3D grande absolute positioned */}
      <div className="hidden xl:flex relative z-10 pointer-events-none xl:absolute xl:inset-0 xl:z-20 xl:items-center xl:justify-end xl:pr-[6rem] 2xl:pr-[5.5rem]">
        {/* Placeholder statico SSR-safe (identico server/client first paint) */}
        <div
          className={`pointer-events-none ${
            mounted && heroSceneReady ? "opacity-0" : "opacity-100"
          } transition-opacity duration-300`}
        >
          <div className="relative flex h-[540px] w-[540px] 2xl:h-[660px] 2xl:w-[660px] items-center justify-center">
            <div className="absolute inset-[-28px] rounded-full bg-primary/18 blur-3xl" />
            <div className="absolute inset-[-10px] rounded-full border border-primary/10" />
            <div className="relative flex h-[220px] w-[220px] items-center justify-center rounded-full border border-primary/15 bg-[radial-gradient(circle_at_center,hsl(var(--primary)/0.18),transparent_65%)] lg:h-[300px] lg:w-[300px]">
              <div className="relative flex h-[168px] w-[168px] items-center justify-center rounded-full border border-primary/18 bg-[radial-gradient(circle_at_center,hsl(var(--primary)/0.24),transparent_70%)] lg:h-[220px] lg:w-[220px]">
                <span className="font-mono text-[0.95rem] font-semibold tracking-[0.14em] text-white">
                  {mounted ? heroSceneProgress : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>
        {heroTier === "desktop" && shouldRenderDesktopScene ? (
          <div
            className={`absolute inset-0 flex items-center justify-end xl:pr-[6rem] 2xl:pr-[5.5rem] transition-opacity duration-300 ${
              heroSceneReady ? "opacity-100" : "opacity-0"
            }`}
          >
            <Hero3DScene
              className="relative z-10 flex items-center justify-center pointer-events-none"
              canvasClassName="w-[540px] h-[540px] 2xl:w-[660px] 2xl:h-[660px] pointer-events-none"
              onLoadingProgress={handleHeroSceneProgress}
              onSceneReady={handleHeroSceneReady}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
